// Send money to a beneficiary (§7): three steps on one screen — pick the
// payee, enter the amount, review — then the MPIN or UPI PIN (§9) and the
// outcome (§10). The idempotency key is minted when the review opens and
// reused on retries (§7.3).
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useSelector } from 'react-redux';
import Modal from 'react-bootstrap/Modal';
import { v4 as uuidv4 } from 'uuid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { listBeneficiaries } from '@/api/beneficiaries';
import { checkLimits, executePayment, getFxQuote, getPaymentContext } from '@/api/payments';
import { getCountries } from '@/api/reference';
import { ApiError } from '@/api/client';
import type { RootState } from '@/store';
import type {
  Beneficiary,
  BeneficiaryAccount,
  CountryRef,
  FxQuote,
  PaymentContext,
  PaymentRail,
} from '@/types/phase2.types';
import { formatIndianCurrency, maskAccountNumber } from '@/utils/currency';
import {
  RAIL_HINTS,
  RAIL_LABELS,
  formatWholeRupees,
  isPayableAccount,
  railDisabledReason,
  railsForAccount,
  suggestRail,
} from '@/utils/payments';
import { filterAmountInput } from '@/utils/phase2Validation';
import { INTERNATIONAL_CHARGE_RUPEES, LRS_PURPOSES } from '@/mock/data';
import { formatShort } from '@/mock/clock';
import Loader from '@/components/ui/Loader/Loader';
import Button from '@/components/ui/Button/Button';
import AccountStatusChip from '@/pages/Beneficiaries/AccountStatusChip';
import { usePaymentAuth } from '../components/usePaymentAuth';
import PaymentAuthSheets from '../components/PaymentAuthSheets';
import {
  KIND_TITLES,
  SUB_TYPE_LABELS,
  accountDestination,
  accountHolderName,
} from '@/pages/Beneficiaries/beneficiaryDisplay';
import '../pay.css';

interface PayeeSelection {
  beneficiary: Beneficiary;
  account: BeneficiaryAccount;
}

type Step = 'payee' | 'amount' | 'review';

const PIN_ERROR_CODES = ['PIN_001', 'PIN_002', 'PIN_003', 'PIN_004'];

// No MPIN yet, or a locked one: the pad cannot help, so the review offers
// MPIN setup instead (setting an MPIN also clears the lock).
const MPIN_SETUP_ERRORS: Record<string, string> = {
  PIN_001: 'You have not set an MPIN yet. Set your MPIN first.',
  PIN_003: 'Your MPIN is locked after too many wrong tries. Set a new MPIN to unlock it.',
};
const MPIN_SETUP_PATH = '/settings/mpin?return=/pay/send';

const productTypeOf = (accountType?: string): PaymentContext['productType'] => {
  const value = (accountType ?? '').toUpperCase();
  if (value.includes('CURRENT')) return 'CURRENT';
  if (value.includes('SALARY')) return 'SALARY';
  return 'SAVINGS';
};

const SendMoney = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selectedAccount = useSelector((state: RootState) => state.account.selectedAccount);

  const [step, setStep] = useState<Step>('payee');
  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[] | null>(null);
  const [countries, setCountries] = useState<CountryRef[]>([]);
  const [query, setQuery] = useState('');
  const [accountPickerFor, setAccountPickerFor] = useState<Beneficiary | null>(null);

  const [selection, setSelection] = useState<PayeeSelection | null>(null);
  const [context, setContext] = useState<PaymentContext | null>(null);
  const [amountStr, setAmountStr] = useState('');
  const [rail, setRail] = useState<PaymentRail | undefined>(undefined);
  const [purpose, setPurpose] = useState('');
  const [remark, setRemark] = useState('');
  const [limitError, setLimitError] = useState('');
  const [continuing, setContinuing] = useState(false);
  const [fx, setFx] = useState<FxQuote | null>(null);
  const [approvalLevels, setApprovalLevels] = useState(0);

  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [reviewError, setReviewError] = useState('');
  const [offerMpinSetup, setOfferMpinSetup] = useState(false);

  const loadContext = useCallback(async () => {
    try {
      const ctx = await getPaymentContext({
        balanceRupees: selectedAccount?.availableBalance,
        productType: productTypeOf(selectedAccount?.accountType),
        accountNumber: selectedAccount?.accountNumber,
      });
      setContext(ctx);
    } catch {
      setContext(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listBeneficiaries()
      .then((list) => {
        setBeneficiaries(list);
        // Repeat support: /pay/send?beneficiaryId=&accountId= (§3).
        const beneficiaryId = searchParams.get('beneficiaryId');
        if (beneficiaryId) {
          const beneficiary = list.find((b) => b.id === beneficiaryId);
          const account = beneficiary?.accounts.find(
            (a) => (searchParams.get('accountId') ? a.id === searchParams.get('accountId') : isPayableAccount(a))
          );
          if (beneficiary && account) {
            setSelection({ beneficiary, account });
            setStep('amount');
          }
        }
      })
      .catch(() => setBeneficiaries([]));
    getCountries().then(setCountries).catch(() => setCountries([]));
    void loadContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- derived ----------

  const payableAccounts = (b: Beneficiary) => b.accounts.filter(isPayableAccount);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (beneficiaries ?? [])
      .filter((b) => payableAccounts(b).length > 0)
      .filter((b) => !q || b.alias.toLowerCase().includes(q))
      .sort((a, b) => a.alias.localeCompare(b.alias, undefined, { sensitivity: 'base' }));
     
  }, [beneficiaries, query]);

  const rails = useMemo(
    () => (selection ? railsForAccount(selection.account) : []),
    [selection]
  );

  const amount = Number(amountStr);
  const amountValid = amountStr !== '' && Number.isFinite(amount) && amount > 0;
  const isInternational = selection?.account.kind === 'INTERNATIONAL';

  // Rail suggestion (§7.2): IMPS below ₹2 lakh, RTGS from ₹2 lakh, unless the
  // customer's pick still fits.
  useEffect(() => {
    if (rails.length > 0 && amountValid) {
      setRail((current) => suggestRail(rails, amount, current));
    } else if (rails.length === 1) {
      setRail(rails[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amountStr, rails]);

  // Mint one idempotency key per review open; retries reuse it (§7.3).
  useEffect(() => {
    if (step === 'review') setIdempotencyKey(uuidv4());
  }, [step]);

  // ---------- payee step ----------

  const pickBeneficiary = (b: Beneficiary) => {
    const payable = payableAccounts(b);
    if (payable.length === 1) {
      selectAccount(b, payable[0]);
    } else {
      setAccountPickerFor(b);
    }
  };

  const selectAccount = (beneficiary: Beneficiary, account: BeneficiaryAccount) => {
    setSelection({ beneficiary, account });
    setAccountPickerFor(null);
    setAmountStr('');
    setRail(undefined);
    setPurpose('');
    setRemark('');
    setLimitError('');
    setFx(null);
    setApprovalLevels(0);
    setStep('amount');
    void loadContext();
  };

  // ---------- amount step ----------

  // The account the money leaves: the one the server resolved, else the
  // account selected on the dashboard.
  const debitAccountNumber = context?.accountNumber ?? selectedAccount?.accountNumber;
  const beneficiaryAccountNumber = selection?.account.kind === 'LOCAL_TRANSFER' ? selection.account.accountNumber : undefined;
  const beneficiaryIfsc = selection?.account.kind === 'LOCAL_TRANSFER' ? selection.account.ifsc : undefined;

  const handleContinue = async () => {
    if (!selection || !rail || !amountValid) return;
    if (isInternational && !purpose) return;
    setLimitError('');
    setContinuing(true);
    try {
      const charges = isInternational ? INTERNATIONAL_CHARGE_RUPEES : 0;
      const result = await checkLimits({
        rail,
        amountRupees: amount,
        chargesRupees: charges,
        account: selection.account,
        debitAccountNumber,
        beneficiaryIfsc,
        beneficiaryName: accountHolderName(selection.account),
        remarks: remark.trim() || undefined,
      });
      if (!result.ok) {
        setLimitError(result.message);
        return;
      }
      setApprovalLevels(result.approvalRequired ? result.approvalLevels : 0);
      if (isInternational) {
        const quote = await getFxQuote(
          selection.account.kind === 'INTERNATIONAL' ? selection.account.currency : 'USD'
        );
        setFx(quote);
      } else {
        setFx(null);
      }
      setStep('review');
    } catch (err) {
      setLimitError(err instanceof ApiError ? err.message : 'Could not check the limit. Try again.');
    } finally {
      setContinuing(false);
    }
  };

  // ---------- review + auth ----------

  const payeeName = selection ? accountHolderName(selection.account) : '';
  const destination = selection ? accountDestination(selection.account, countries) : '';
  const charges = isInternational ? (fx?.chargesRupees ?? INTERNATIONAL_CHARGE_RUPEES) : 0;
  const approvalLine = `Approve ${formatIndianCurrency(amount)} to ${payeeName} via ${rail ? RAIL_LABELS[rail] : ''}`;
  // The server says there is no MPIN yet: send them to set one, not to the pad.
  const mpinMissing = rail !== 'UPI' && context?.mpinSet === false;

  const execute = async (authTicket: string, mpin?: string) => {
    if (!selection || !rail) return;
    setReviewError('');
    setOfferMpinSetup(false);
    try {
      const payment = await executePayment({
        idempotencyKey,
        rail,
        amountRupees: amount,
        payeeName,
        destination,
        beneficiaryId: selection.beneficiary.id,
        beneficiaryAlias: selection.beneficiary.alias,
        debitAccountNumber,
        beneficiaryAccountNumber,
        beneficiaryIfsc,
        accountId: selection.account.id,
        purpose: purpose || undefined,
        remark: remark.trim() || undefined,
        fx: fx ?? undefined,
        mpin,
        authTicket,
      });
      navigate('/pay/outcome', { state: { payment }, replace: true });
    } catch (err) {
      // Not set or locked: returning closes the pad and the review offers
      // MPIN setup.
      if (rail !== 'UPI' && err instanceof ApiError && MPIN_SETUP_ERRORS[err.code]) {
        setReviewError(MPIN_SETUP_ERRORS[err.code]);
        setOfferMpinSetup(true);
        return;
      }
      // Other MPIN errors keep the pad open with the server's text (attempts
      // left); the same review and idempotency key are reused.
      if (rail !== 'UPI' && err instanceof ApiError && PIN_ERROR_CODES.includes(err.code)) throw err;
      setReviewError(err instanceof ApiError ? err.message : 'Payment failed. Try again.');
    }
  };

  const auth = usePaymentAuth(execute, { mpinInPaymentBody: true });

  const startAuth = async () => {
    if (!rail || mpinMissing) return;
    setReviewError('');
    setOfferMpinSetup(false);
    const error = await auth.start(
      rail,
      approvalLine,
      rail === 'UPI' && selection?.account.kind === 'MOBILE_TRANSFER' && selection.account.subType === 'UPI_ID'
        ? selection.account.upiId
        : undefined,
    );
    if (error) setReviewError(error);
  };

  const goBack = () => {
    if (step === 'review') setStep('amount');
    else if (step === 'amount') setStep('payee');
    else navigate('/pay');
  };

  const titles: Record<Step, string> = {
    payee: 'Send money',
    amount: 'Enter amount',
    review: 'Review payment',
  };

  // ---------- render ----------

  return (
    <div className="nova-pay-flow">
      <div className="nova-pay-flow-header">
        <button type="button" className="nova-icon-btn" title="Back" aria-label="Back" onClick={goBack}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </button>
        <h2>{titles[step]}</h2>
      </div>

      {step === 'payee' && (
        <>
          <input
            type="search"
            className="form-control my-3"
            placeholder="search by name"
            aria-label="search by name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />

          {!beneficiaries && <Loader rows={4} />}
          {beneficiaries && beneficiaries.length === 0 && (
            <div className="nova-pay-empty">
              <p>No beneficiaries yet. Add someone you pay often.</p>
              <Button onClick={() => navigate('/beneficiaries/add?from=pay')}>Add beneficiary</Button>
            </div>
          )}
          {beneficiaries && beneficiaries.length > 0 && filtered.length === 0 && (
            <p className="nova-ben-no-match">No beneficiaries match &quot;{query.trim()}&quot;.</p>
          )}
          {filtered.map((b) => (
            <button type="button" key={b.id} className="nova-pay-payee-row" onClick={() => pickBeneficiary(b)}>
              <span className="nova-ben-avatar">{b.alias.trim().charAt(0).toUpperCase()}</span>
              <span className="nova-ben-row-text">
                <span className="nova-ben-alias">{b.alias}</span>
                <span className="nova-ben-count">
                  {payableAccounts(b).length === 1
                    ? '1 payable account'
                    : `${payableAccounts(b).length} payable accounts`}
                </span>
              </span>
              <FontAwesomeIcon icon={faChevronRight} className="nova-pay-chevron" />
            </button>
          ))}
        </>
      )}

      {step === 'amount' && selection && (
        <>
          <div className="nova-pay-payee-header">
            <span className="nova-ben-avatar">{payeeName.charAt(0).toUpperCase()}</span>
            <span className="nova-ben-row-text">
              <span className="nova-ben-alias">{payeeName}</span>
              <span className="nova-ben-count">{destination}</span>
            </span>
            <AccountStatusChip status={selection.account.status} />
          </div>

          <div className="mb-3">
            <label className="form-label">Amount</label>
            <div className="input-group">
              <span className="input-group-text">₹</span>
              <input
                type="text"
                inputMode="decimal"
                className={`form-control ${amountStr !== '' && !amountValid ? 'is-invalid' : ''}`}
                value={amountStr}
                onChange={(e) => setAmountStr(filterAmountInput(e.target.value, amountStr))}
              />
            </div>
            {amountStr !== '' && !amountValid && (
              <div className="invalid-feedback d-block">Invalid Amount. Please enter valid amount.</div>
            )}
          </div>

          {context && (
            <p className="nova-pay-context-line">
              Available {formatWholeRupees(context.balanceRupees)}
              {context.dailyLimitRupees > 0 && (
                <>
                  {' '}· Today {formatWholeRupees(context.dailyUsedRupees)} of{' '}
                  {formatWholeRupees(context.dailyLimitRupees)} used
                </>
              )}
            </p>
          )}

          {rails.length > 1 && (
            <div className="mb-3">
              <label className="form-label">Transfer mode</label>
              <div className="nova-rail-list">
                {rails.map((r) => {
                  const reason = amountValid ? railDisabledReason(r, amount) : null;
                  return (
                    <button
                      type="button"
                      key={r}
                      className={`nova-rail-option ${rail === r ? 'active' : ''}`}
                      disabled={Boolean(reason)}
                      onClick={() => setRail(r)}
                    >
                      <span className="nova-rail-name">{RAIL_LABELS[r]}</span>
                      <span className="nova-rail-hint">{reason ?? RAIL_HINTS[r]}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {isInternational && (
            <>
              <div className="mb-3">
                <label className="form-label">Purpose</label>
                <select className="form-select" value={purpose} onChange={(e) => setPurpose(e.target.value)}>
                  <option value="">-- Select purpose --</option>
                  {LRS_PURPOSES.map((p) => (
                    <option key={p.code} value={`${p.label} (${p.code})`}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <div className="form-text">
                  Sent in {selection.account.kind === 'INTERNATIONAL' ? selection.account.currency : ''}. The rate and
                  charges are shown on the next step.
                </div>
                <div className="form-text">
                  International transfers follow the Liberalised Remittance Scheme (up to USD 2,50,000 a year).
                </div>
              </div>
            </>
          )}

          <div className="mb-3">
            <label className="form-label">Remark (optional)</label>
            <input
              type="text"
              className="form-control"
              placeholder="What is this for?"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
            />
          </div>

          {limitError && <div className="nova-limit-box">{limitError}</div>}
          {context?.newDeviceUntil && (
            <p className="nova-pay-newdevice">
              New device: limited to ₹50,000 until {formatShort(context.newDeviceUntil)}.
            </p>
          )}

          <Button
            onClick={() => void handleContinue()}
            disabled={!amountValid || !rail || (isInternational && !purpose)}
            isLoading={continuing}
          >
            Continue
          </Button>
        </>
      )}

      {step === 'review' && selection && rail && (
        <>
          <div className="nova-review-amount">
            <span className="nova-review-amount-value">{formatIndianCurrency(amount)}</span>
            <span className="nova-review-amount-to">to {payeeName}</span>
          </div>
          <dl className="nova-receipt-rows mb-4">
            <div className="nova-receipt-row"><dt>From</dt><dd>{debitAccountNumber ? maskAccountNumber(debitAccountNumber) : 'Your NovaBank account'}</dd></div>
            <div className="nova-receipt-row"><dt>To</dt><dd>{payeeName}</dd></div>
            <div className="nova-receipt-row"><dt>Beneficiary</dt><dd>{selection.beneficiary.alias}</dd></div>
            <div className="nova-receipt-row"><dt>Destination</dt><dd>{destination}</dd></div>
            <div className="nova-receipt-row"><dt>Mode</dt><dd>{RAIL_LABELS[rail]}</dd></div>
            <div className="nova-receipt-row"><dt>Amount</dt><dd>{formatIndianCurrency(amount)}</dd></div>
            <div className="nova-receipt-row"><dt>Charges</dt><dd>{formatIndianCurrency(charges)}</dd></div>
            {fx && (
              <>
                <div className="nova-receipt-row">
                  <dt>They receive</dt>
                  <dd>
                    {fx.currency}{' '}
                    {new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
                      amount / (fx.ratePaise / 100)
                    )}
                  </dd>
                </div>
                <div className="nova-receipt-row">
                  <dt>Rate</dt>
                  <dd>
                    {formatIndianCurrency(fx.ratePaise / 100)} per {fx.currency}
                  </dd>
                </div>
                <div className="nova-receipt-row">
                  <dt>Total debit</dt>
                  <dd>{formatIndianCurrency(amount + fx.chargesRupees)}</dd>
                </div>
              </>
            )}
            {purpose && <div className="nova-receipt-row"><dt>Purpose</dt><dd>{purpose}</dd></div>}
            {remark.trim() && <div className="nova-receipt-row"><dt>Remark</dt><dd>{remark.trim()}</dd></div>}
          </dl>

          {approvalLevels > 0 && (
            <p className="nova-review-note nova-review-approval-note">
              This payment needs bank approval ({approvalLevels} {approvalLevels === 1 ? 'approval' : 'approvals'}).
            </p>
          )}
          <p className="nova-review-note">
            {rail === 'UPI'
              ? "You'll confirm with your UPI PIN."
              : "You'll confirm with your MPIN."}
          </p>
          {reviewError && (
            <div className="nova-limit-box">
              {reviewError}
              {offerMpinSetup && (
                <button
                  type="button"
                  className="btn btn-link p-0 ms-1 align-baseline"
                  onClick={() => navigate(MPIN_SETUP_PATH)}
                >
                  Set your MPIN
                </button>
              )}
            </div>
          )}

          {mpinMissing ? (
            <>
              <div className="nova-limit-box">
                <strong>Set your MPIN first</strong>
                <div>You need an MPIN to approve this payment.</div>
              </div>
              <div className="nova-form-footer">
                <Button variant="outline" onClick={() => setStep('amount')}>
                  Back
                </Button>
                <Button onClick={() => navigate(MPIN_SETUP_PATH)}>
                  Set your MPIN
                </Button>
              </div>
            </>
          ) : (
            <div className="nova-form-footer">
              <Button variant="outline" onClick={() => setStep('amount')}>
                Back
              </Button>
              <Button onClick={() => void startAuth()}>{rail === 'UPI' ? 'Pay with UPI PIN' : 'Pay'}</Button>
            </div>
          )}
        </>
      )}

      {/* Account picker (§7.1.4) */}
      <Modal show={Boolean(accountPickerFor)} onHide={() => setAccountPickerFor(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Pay {accountPickerFor?.alias} to</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {accountPickerFor &&
            payableAccounts(accountPickerFor).map((account) => (
              <button
                type="button"
                key={account.id}
                className="nova-pay-account-row"
                onClick={() => selectAccount(accountPickerFor, account)}
              >
                <span className="nova-ben-row-text">
                  <span className="nova-ben-alias">
                    {account.kind === 'MOBILE_TRANSFER' ? SUB_TYPE_LABELS[account.subType] : KIND_TITLES[account.kind]}
                  </span>
                  <span className="nova-ben-count">{accountDestination(account, countries)}</span>
                </span>
                <AccountStatusChip status={account.status} />
              </button>
            ))}
        </Modal.Body>
      </Modal>

      {/* MPIN + OTP / UPI PIN sheets (§9) */}
      <PaymentAuthSheets
        authState={auth.authState}
        approvalLine={approvalLine}
        pinError={auth.pinError}
        pinResetKey={auth.pinResetKey}
        pinLength={auth.pinLength}
        onPinLengthChange={auth.setPinLength}
        otpExpiry={auth.otpExpiry}
        otpSessionKey={auth.otpSessionKey}
        onCompletePin={(pin) => void auth.completePin(pin)}
        onVerifyOtp={auth.verifyOtp}
        onResendOtp={auth.resendOtp}
        onClose={auth.close}
      />
    </div>
  );
};

export default SendMoney;
